// Calculate player form grade based on performance stats
// A = Excellent (Win rate >= 60%)
// B = Good (Win rate 45-59%)
// C = Average (Win rate 30-44%)
// D = Poor (Win rate < 30%)

export interface FormGrade {
  grade: 'A' | 'B' | 'C' | 'D';
  label: string;
  color: string;
  bgColor: string;
  description: string;
}

interface PlayerStats {
  wins?: number;
  draws?: number;
  losses?: number;
  matches?: number;
  goals?: number;
  points?: number;
}

export function calculateForm(stats: PlayerStats | null | undefined): FormGrade {
  if (!stats) {
    return {
      grade: 'C',
      label: 'Average',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20 border-amber-500/50',
      description: 'No matches played yet'
    };
  }

  const matches = stats.matches || 0;
  const wins = stats.wins || 0;
  const draws = stats.draws || 0;

  // Need at least 1 match for form calculation
  if (matches === 0) {
    return {
      grade: 'C',
      label: 'Average',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20 border-amber-500/50',
      description: 'No matches played yet'
    };
  }

  // Calculate win rate (wins count as 1, draws as 0.5)
  const effectiveWins = wins + (draws * 0.5);
  const winRate = (effectiveWins / matches) * 100;

  if (winRate >= 60) {
    return {
      grade: 'A',
      label: 'Excellent',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20 border-emerald-500/50',
      description: `${Math.round(winRate)}% win rate - Outstanding performance`
    };
  } else if (winRate >= 45) {
    return {
      grade: 'B',
      label: 'Good',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20 border-blue-500/50',
      description: `${Math.round(winRate)}% win rate - Solid performance`
    };
  } else if (winRate >= 30) {
    return {
      grade: 'C',
      label: 'Average',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20 border-amber-500/50',
      description: `${Math.round(winRate)}% win rate - Room for improvement`
    };
  } else {
    return {
      grade: 'D',
      label: 'Poor',
      color: 'text-red-400',
      bgColor: 'bg-red-500/20 border-red-500/50',
      description: `${Math.round(winRate)}% win rate - Needs improvement`
    };
  }
}

export function getFormBadgeStyles(grade: 'A' | 'B' | 'C' | 'D') {
  switch (grade) {
    case 'A':
      return {
        gradient: 'from-emerald-500 to-green-600',
        glow: 'shadow-emerald-500/50',
        text: 'text-white'
      };
    case 'B':
      return {
        gradient: 'from-blue-500 to-cyan-600',
        glow: 'shadow-blue-500/50',
        text: 'text-white'
      };
    case 'C':
      return {
        gradient: 'from-amber-500 to-orange-600',
        glow: 'shadow-amber-500/50',
        text: 'text-white'
      };
    case 'D':
      return {
        gradient: 'from-red-500 to-rose-600',
        glow: 'shadow-red-500/50',
        text: 'text-white'
      };
  }
}
